import { createRoute, Link, useNavigate } from "@tanstack/solid-router";
import { highlightTextColor, SEARCH_MATCH_COLOR } from "@bso/shared";
import { plainVerseText } from "@/utils/verse-text";
import { appRoute } from "./route";
import { createEffect, createSignal, For, onMount, Show, untrack } from "solid-js";
import { BookOpen, ChevronDown, ChevronUp, Filter, Search as SearchIcon, X } from "@fleet/icons";
import { bibleBooks, bookGroups, getBookNameDescriptor, idToAbbr } from "@/utils/bible-books-data";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { api } from "@/lib/api";
import { EmptyIllustration } from "@/components/ui/empty-illustration";
import { NativeAdCard } from "@/features/growth/components/native-ad-card";
import { getLastReadBible } from "@/utils/last-read";
import type { components } from "@bso/client";

type VerseResponse = components["schemas"]["VerseResponse"];
type SearchCursor = components["schemas"]["SearchCursor"];

// Fallback version when the URL doesn't pin one: the version the user is
// currently reading (legacy parity: the searcher used the active bible version).
function getActiveVersion(): string {
  return getLastReadBible().version;
}

const VERSION_PATTERN = /^[a-z0-9]{2,16}$/i;

/** Sanitizes `?v=` (version abbreviation) to lowercase; omits invalid values. */
function parseVersionParam(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const v = value.trim().toLowerCase();
  return VERSION_PATTERN.test(v) ? v : undefined;
}

/**
 * Serialized pagination cursor for the URL (`?cursor=book.chapter.verse`).
 * Makes the results page **reproducible**: pasting the URL resumes where the
 * user was. Returns `null` for incomplete/invalid values. The version is not
 * included in this string (it lives in `?v=`); the caller injects it when resuming.
 */
function parseCursorParam(value: unknown, version: string): SearchCursor | null {
  if (typeof value !== "string") {
    return null;
  }
  const parts = value.split(".");
  if (parts.length !== 3) {
    return null;
  }
  const book = Number.parseInt(parts[0] ?? "", 10);
  const chapter = Number.parseInt(parts[1] ?? "", 10);
  const verse = Number.parseInt(parts[2] ?? "", 10);
  if (!Number.isInteger(book) || !Number.isInteger(chapter) || !Number.isInteger(verse)) {
    return null;
  }
  return { book, chapter, verse, version };
}

/** Is `?cursor=` syntactically valid? (sanitized in validateSearch, without version). */
function isValidCursorString(value: unknown): boolean {
  return parseCursorParam(value, "") !== null;
}

function serializeCursor(cursor: SearchCursor | null): string | undefined {
  if (!cursor) {
    return undefined;
  }
  return `${cursor.book}.${cursor.chapter}.${cursor.verse}`;
}

/** Parses the `books` parameter (CSV of ids) into a sanitized numeric list. */
function parseBooks(value: string | undefined): number[] {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);
}

function escapeRegExp(string: string) {
  return string.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightMatch(text: string, query: string) {
  // Raw text contains Strong and formatting marks; they are cleaned with the SAME
  // tokenizer used by the reader (previously only Strong marks were removed here, so
  // a `<FI>` ended up printed in search results).
  const cleanText = plainVerseText(text);
  if (!query.trim()) {
    return <span>{cleanText}</span>;
  }

  try {
    const parts = cleanText.split(new RegExp(`(${escapeRegExp(query)})`, "gi"));
    return (
      <For each={parts}>
        {(part) => {
          if (part.toLowerCase() === query.toLowerCase()) {
            return (
              // PASTEL yellow, not the legacy pure yellow: it preserves the
              // "amarillo" reading of the published app with 14,4:1 contrast
              // instead of aggressive `#FFFF00`.
              <mark
                class="font-semibold px-0.5 rounded"
                style={{
                  "background-color": SEARCH_MATCH_COLOR,
                  color: highlightTextColor(SEARCH_MATCH_COLOR),
                }}
              >
                {part}
              </mark>
            );
          }
          return <span>{part}</span>;
        }}
      </For>
    );
  } catch {
    return <span>{cleanText}</span>;
  }
}

export function SearchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = searchRoute.useSearch();

  // The term, concordance mode, and filtered books live in the URL
  // (`?q=&t=&books=`) so a search is **shareable/reloadable**: loading
  // from a URL reconstructs the state and reruns the search. Local signals
  // are the reactive source for rendering, initialized from the URL.
  const [searchText, setSearchText] = createSignal(search().q ?? "");
  const [selectedBooks, setSelectedBooks] = createSignal<number[]>(parseBooks(search().books));
  const [items, setItems] = createSignal<VerseResponse[]>([]);
  const [nextCursor, setNextCursor] = createSignal<SearchCursor | null>(null);
  const [hasMore, setHasMore] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  const [showFilters, setShowFilters] = createSignal(parseBooks(search().books).length > 0);
  const [concordance, setConcordance] = createSignal<"EXACT" | "FUZZY">(
    search().t === "EXACT" ? "EXACT" : "FUZZY",
  );
  const [bookCounts, setBookCounts] = createSignal<Map<number, number> | null>(null);

  // Version used in the search. The URL (`?v=`) sets it **deterministically**
  // so the same URL gives anyone the same results (gap #24): without the
  // param it falls back to the last-read version, but during a search it is anchored in the URL.
  const searchVersion = (): string => parseVersionParam(search().v) ?? getActiveVersion();

  // Writes the shareable search state to the URL (idempotent, without bloating
  // history). It runs after every change affecting WHAT is searched. `cursorOverride`
  // allows anchoring the current page (`?cursor=`) when loading more results.
  function syncUrl(cursorOverride?: SearchCursor | null) {
    const q = searchText().trim();
    const books = selectedBooks();
    void navigate({
      to: "/search",
      search: {
        q: q === "" ? undefined : q,
        t: concordance() === "EXACT" ? "EXACT" : undefined,
        books: books.length > 0 ? books.join(",") : undefined,
        // The version is anchored in the URL only when there is a term (a real search),
        // so an empty URL is not polluted with the visitor's last-read version.
        v: q === "" ? undefined : searchVersion(),
        cursor: cursorOverride === undefined ? search().cursor : serializeCursor(cursorOverride),
      },
      replace: true,
    });
  }

  // URL → signals (back/forward + pasted link). The URL value wins.
  // The comparison is wrapped in `untrack` so the effect reacts ONLY to changes
  // EXTERNAL to the URL; if it tracks `searchText()` (the signal that writes), a
  // state-first change retriggers the effect with the old URL and reverts the
  // input (bug #15b / CC-16, already fixed in font-manager.tsx).
  createEffect(() => {
    const urlQuery = search().q ?? "";
    if (untrack(() => urlQuery !== searchText())) {
      setSearchText(urlQuery);
    }
  });

  const totalResults = () => {
    const counts = bookCounts();
    if (!counts) {
      return null;
    }
    let total = 0;
    for (const count of counts.values()) {
      total += count;
    }
    return total;
  };

  // Per-book result counts (legacy parity: use-bible-search-books-result-count)
  async function fetchBookCounts(text: string) {
    try {
      const { data, response } = await api.GET("/api/v1/bible/verses/search-count", {
        params: {
          query: {
            version: searchVersion(),
            text,
            t: concordance(),
            books: null,
          },
        },
      });
      if (response.ok && data) {
        const map = new Map<number, number>();
        for (const row of data) {
          map.set(row.book, row.count);
        }
        setBookCounts(map);
      }
    } catch (e) {
      console.warn("Failed to fetch per-book result counts", e);
    }
  }

  const toggleCategory = (ids: number[]) => {
    const allSelected = ids.every((id) => selectedBooks().includes(id));
    if (allSelected) {
      setSelectedBooks((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedBooks((prev) => {
        const base = prev.filter((id) => !ids.includes(id));
        return [...base, ...ids];
      });
    }
    syncUrl(null);
  };

  const handleSearch = async (reset = true, cursorOverride?: SearchCursor | null) => {
    if (!searchText().trim()) {
      return;
    }

    // `reset` starts at page 1 (clears the URL cursor). `cursorOverride`
    // (deep-link `?cursor=`) resumes at that page. Otherwise, use the cursor at hand.
    const currentCursor = reset ? null : (cursorOverride ?? nextCursor());

    if (reset) {
      syncUrl(null);
    } else {
      syncUrl(currentCursor);
    }
    setLoading(true);
    try {
      const booksParam = selectedBooks().length > 0 ? selectedBooks().join(",") : null;

      if (reset) {
        setBookCounts(null);
        void fetchBookCounts(searchText());
      }

      const { data, response } = await api.GET("/api/v1/bible/verses/search", {
        params: {
          query: {
            version: searchVersion(),
            text: searchText(),
            t: concordance(),
            books: booksParam,
            book: currentCursor?.book ?? null,
            chapter: currentCursor?.chapter ?? null,
            verse: currentCursor?.verse ?? null,
            cursorDirection: currentCursor ? "NEXT" : null,
          },
        },
      });

      if (!response.ok) {
        console.error("Failed to search verses");
        return;
      }

      if (data) {
        if (reset) {
          setItems(data.items);
        } else {
          setItems((prev) => [...prev, ...data.items]);
        }
        setNextCursor(data.nextCursor ?? null);
        setHasMore(data.hasNextPage);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleBookFilter = (id: number) => {
    if (selectedBooks().includes(id)) {
      setSelectedBooks((prev) => prev.filter((b) => b !== id));
    } else {
      setSelectedBooks((prev) => [...prev, id]);
    }
    syncUrl(null);
  };

  const clearFilters = () => {
    setSelectedBooks([]);
    syncUrl(null);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      void handleSearch(true);
    }
  };

  // Deep link: if the URL contains a term, rerun the search on mount to
  // reconstruct the results (idempotent when reloading/pasting the link). If the
  // URL also contains `?cursor=`, resume at that page instead of page 1.
  onMount(() => {
    if (searchText().trim()) {
      const bootCursor = parseCursorParam(search().cursor, searchVersion());
      void (bootCursor ? handleSearch(false, bootCursor) : handleSearch(true));
    }
  });

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <SearchIcon class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Bible Search`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`Fast full-text search (FTS)`)}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div class="flex-1 overflow-y-auto p-4 md:p-8 max-w-4xl mx-auto w-full flex flex-col space-y-6 pb-24">
        {/* Search controls */}
        <div class="space-y-4 bg-card/40 border border-border/30 rounded-2xl p-5 shadow-sm">
          <div class="flex gap-3">
            <div class="relative flex-1">
              <SearchIcon class="absolute left-4 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-muted-foreground" />
              <input
                type="text"
                placeholder={t(msg`Search by word or phrase (e.g.: 'grace', 'beginning')...`)}
                value={searchText()}
                onInput={(e) => setSearchText(e.target.value)}
                onKeyDown={handleKeyDown}
                class="w-full pl-11 pr-4 py-3 bg-background border border-border/50 rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                void handleSearch(true);
              }}
              disabled={loading() || !searchText().trim()}
              class="px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/95 disabled:opacity-50 text-sm font-semibold rounded-xl transition-all shadow-md active:scale-98"
            >
              {loading() && items().length === 0 ? t(msg`Searching...`) : t(msg`Search`)}
            </button>
          </div>

          {/* Concordance type controller */}
          <div class="flex items-center gap-3 border-t border-border/20 pt-3">
            <span class="text-2xs font-semibold text-muted-foreground">{t(msg`Concordance:`)}</span>
            <div class="flex bg-muted/40 border border-border/30 p-0.5 rounded-lg shrink-0">
              <button
                type="button"
                onClick={() => {
                  setConcordance("FUZZY");
                  syncUrl(null);
                }}
                class="px-3 py-1 text-3xs font-bold rounded-md transition-all"
                classList={{
                  "bg-primary text-primary-foreground shadow-xs": concordance() === "FUZZY",
                  "text-muted-foreground hover:text-foreground": concordance() !== "FUZZY",
                }}
              >
                {t(msg`Partial (Fuzzy)`)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConcordance("EXACT");
                  syncUrl(null);
                }}
                class="px-3 py-1 text-3xs font-bold rounded-md transition-all"
                classList={{
                  "bg-primary text-primary-foreground shadow-xs": concordance() === "EXACT",
                  "text-muted-foreground hover:text-foreground": concordance() !== "EXACT",
                }}
              >
                {t(msg`Exact (Word)`)}
              </button>
            </div>
          </div>

          {/* Collapsible Book Filters */}
          <div class="border-t border-border/30 pt-3">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters())}
              class="flex items-center justify-between w-full py-1.5 px-2.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted/30 transition-colors"
            >
              <div class="flex items-center gap-2">
                <Filter class="h-3.5 w-3.5" />
                <span>
                  {t(msg`Filter by books`)}{" "}
                  {selectedBooks().length > 0
                    ? `(${selectedBooks().length} ${t(msg`selected`)})`
                    : ""}
                </span>
              </div>
              <Show when={showFilters()} fallback={<ChevronDown class="h-3.5 w-3.5" />}>
                <ChevronUp class="h-3.5 w-3.5" />
              </Show>
            </button>

            <Show when={showFilters()}>
              <div class="mt-3 space-y-4">
                {/* Canonical Groups */}
                <div class="space-y-2 border-b border-border/20 pb-3">
                  <span class="text-2xs text-muted-foreground font-semibold">
                    {t(msg`Advanced Canonical Groups:`)}
                  </span>
                  <div class="flex flex-wrap gap-1.5">
                    <For each={bookGroups}>
                      {(group) => {
                        const isAllSelected = () =>
                          group.ids.every((id) => selectedBooks().includes(id));
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              toggleCategory(group.ids);
                            }}
                            class="px-2.5 py-1 text-3xs font-bold border rounded-lg transition-all"
                            classList={{
                              "bg-primary text-primary-foreground border-primary shadow-xs":
                                isAllSelected(),
                              "bg-background hover:bg-muted/40 border-border/40 text-foreground/80":
                                !isAllSelected(),
                            }}
                          >
                            {t(group.name)}
                          </button>
                        );
                      }}
                    </For>
                  </div>
                </div>

                <div class="flex items-center justify-between border-b border-border/20 pb-2">
                  <span class="text-2xs text-muted-foreground font-medium">
                    {t(msg`Select one or more books individually`)}
                  </span>
                  <Show when={selectedBooks().length > 0}>
                    <button
                      type="button"
                      onClick={clearFilters}
                      class="text-2xs font-semibold text-primary hover:underline flex items-center gap-1"
                    >
                      <X class="w-3 h-3" />
                      <span>{t(msg`Clear filters`)}</span>
                    </button>
                  </Show>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1">
                  <For each={bibleBooks}>
                    {(book) => (
                      <button
                        type="button"
                        onClick={() => {
                          toggleBookFilter(book.id);
                        }}
                        class="px-2 py-1.5 text-3xs font-medium border rounded-lg text-left transition-all truncate flex items-center justify-between gap-1"
                        classList={{
                          "bg-primary/10 border-primary text-primary": selectedBooks().includes(
                            book.id,
                          ),
                          "bg-background hover:bg-muted/50 border-border/40 text-foreground/80":
                            !selectedBooks().includes(book.id),
                        }}
                      >
                        <span class="truncate">{t(book.name)}</span>
                        <Show when={(bookCounts()?.get(book.id) ?? 0) > 0}>
                          <span class="shrink-0 px-1 rounded-md bg-primary/15 text-primary font-bold text-4xs">
                            {bookCounts()?.get(book.id)}
                          </span>
                        </Show>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </div>
        </div>

        {/* Chips for the ACTIVE filter, removable with ✕ (parity with
          `RenderSearchFilterBarItem`). "Entire Bible" es el chip fijo que no se
          puede quitar: es el estado "sin filtro", no un filtro más. */}
        <div class="flex flex-wrap items-center gap-1.5" data-testid="search-filter-chips">
          <Show
            when={selectedBooks().length > 0}
            fallback={
              <span class="px-2.5 py-1 text-3xs font-bold rounded-full bg-muted/40 border border-border/40 text-foreground/80">
                {t(msg`Entire Bible`)}
              </span>
            }
          >
            <For each={selectedBooks()}>
              {(bookId) => {
                const book = bibleBooks.find((b) => b.id === bookId);
                const label = book ? t(book.name) : String(bookId);
                return (
                  <button
                    type="button"
                    data-testid={`search-filter-chip-${bookId}`}
                    onClick={() => {
                      toggleBookFilter(bookId);
                    }}
                    class="flex items-center gap-1 px-2.5 py-1 text-3xs font-bold rounded-full bg-primary/10 border border-primary/40 text-primary hover:bg-primary/20 transition-colors"
                    aria-label={t(msg`Remove ${label} from the filter`)}
                  >
                    <span>{label}</span>
                    <X class="w-3 h-3" />
                  </button>
                );
              }}
            </For>
          </Show>
        </div>

        {/* Results bar (legacy parity: search-results-bar with total count) */}
        <Show when={totalResults() !== null && items().length > 0}>
          <div class="flex items-center justify-between px-4 py-2.5 rounded-xl bg-muted/20 border border-border/30">
            <span class="text-xs font-semibold text-foreground/85">
              {totalResults()} {totalResults() === 1 ? t(msg`result`) : t(msg`results`)}{" "}
              {t(msg`for`)} «{searchText().trim()}»
            </span>
            <span class="text-3xs font-bold uppercase tracking-wide text-muted-foreground">
              {concordance() === "EXACT" ? t(msg`Exact`) : t(msg`Partial`)} ·{" "}
              {searchVersion().toUpperCase()}
            </span>
          </div>
        </Show>

        {/* Results List */}
        <div class="space-y-4">
          <Show
            when={items().length > 0}
            fallback={
              <Show when={!loading()}>
                <div class="flex flex-col items-center justify-center py-16 border border-dashed border-border/40 rounded-2xl bg-card/10 text-center p-6 motion-safe:animate-fade-in">
                  <EmptyIllustration variant="search" class="mb-3" />
                  <h3 class="text-sm font-bold text-foreground/80">{t(msg`Start your search`)}</h3>
                  <p class="text-xs text-muted-foreground max-w-sm mt-1">
                    {t(msg`Enter terms, keywords or complete phrases to find verses instantly.`)}
                  </p>
                </div>
              </Show>
            }
          >
            <div class="space-y-3">
              <For each={items()}>
                {(item) => (
                  <Link
                    to="/bible/$version/$bookAbbr/$chapter"
                    params={{
                      version: item.version,
                      bookAbbr: idToAbbr(item.book),
                      chapter: String(item.chapter),
                    }}
                    search={{
                      verse: item.verse,
                    }}
                    class="block p-5 bg-card/30 border border-border/30 hover:border-primary/40 rounded-xl transition-all shadow-2xs hover:shadow-sm"
                  >
                    <div class="flex items-center justify-between border-b border-border/20 pb-2 mb-3">
                      <div class="flex items-center gap-2">
                        <BookOpen class="w-3.5 h-3.5 text-primary" />
                        <span class="text-xs font-bold text-foreground/80">
                          {t(getBookNameDescriptor(item.book))} {item.chapter}:{item.verse}
                        </span>
                      </div>
                      <span class="text-3xs px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary uppercase font-bold tracking-wider">
                        {item.version}
                      </span>
                    </div>
                    <p class="text-sm leading-relaxed text-foreground/85 font-serif">
                      {highlightMatch(item.text, searchText())}
                    </p>
                  </Link>
                )}
              </For>
              {/* Native at the END of the results (§10: "fin de búsqueda en
                  catálogo"). Nunca intercalada entre versículos: el usuario está
                  barriendo la Escritura y una card patrocinada en medio de esa
                  lista es indistinguible de un versículo hasta que se lee. */}
              <NativeAdCard placement="search-results" minHeight="6rem" />
            </div>

            {/* Load More Button */}
            <Show when={hasMore()}>
              <div class="pt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    void handleSearch(false);
                  }}
                  disabled={loading()}
                  class="px-6 py-2.5 bg-muted/60 hover:bg-muted text-foreground/85 border border-border/40 text-xs font-semibold rounded-lg shadow-2xs transition-all flex items-center gap-2 disabled:opacity-50 active:scale-98"
                >
                  <Show when={loading()} fallback={<span>{t(msg`Load more results`)}</span>}>
                    <div class="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span>{t(msg`Loading...`)}</span>
                  </Show>
                </button>
              </div>
            </Show>
          </Show>

          {/* Loading Skeletal state */}
          <Show when={loading() && items().length === 0}>
            <div class="space-y-3 animate-pulse">
              <div class="h-24 bg-card/40 border border-border/20 rounded-xl" />
              <div class="h-24 bg-card/40 border border-border/20 rounded-xl" />
              <div class="h-24 bg-card/40 border border-border/20 rounded-xl" />
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}

type SearchParams = {
  /** Search term. */
  q?: string;
  /** Modo de concordancia: `EXACT` (palabra exacta) o ausente (fuzzy/parcial). */
  t?: "EXACT";
  /** Libros filtrados, CSV de ids (p.ej. `1,2,40`). */
  books?: string;
  /** Pinned Bible version for deterministic results (e.g. `kjv`). */
  v?: string;
  /** Pagination cursor `book.chapter.verse` for resuming on a page. */
  cursor?: string;
};

export const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/search",
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    q: typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined,
    t: search.t === "EXACT" ? "EXACT" : undefined,
    books:
      typeof search.books === "string" && parseBooks(search.books).length > 0
        ? parseBooks(search.books).join(",")
        : undefined,
    v: parseVersionParam(search.v),
    // We keep `?cursor=` only if it is syntactically valid; the version is
    // resolved at runtime (`?v=`/last-read), not in this pure sanitization.
    cursor:
      typeof search.cursor === "string" && isValidCursorString(search.cursor)
        ? search.cursor
        : undefined,
  }),
  component: SearchPage,
});
